import React, { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Hammer, Copy, Check, Loader2, AlertTriangle, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EFFORT_STYLES = {
  "Quick win": "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30",
  "Moderate": "bg-amber-500/20 text-amber-300 border border-amber-500/30",
  "Heavy build": "bg-rose-500/20 text-rose-300 border border-rose-500/30",
};

function renderBrief(text) {
  const parts = text.split(/(`?\s*\[Effort: (?:Quick win|Moderate|Heavy build)\]\s*`?)/g);
  return parts.map((p, i) => {
    const m = p.match(/\[Effort: (Quick win|Moderate|Heavy build)\]/);
    if (m) {
      return (
        <span key={i} className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wide mr-1 ${EFFORT_STYLES[m[1]]}`}>
          {m[1]}
        </span>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

export default function BuildBriefDialog({ scanId }) {
  const [open, setOpen] = useState(false);
  const [brief, setBrief] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const fetchBrief = async (refresh = false) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/scans/${scanId}/brief${refresh ? "?refresh=true" : ""}`);
      setBrief(data.brief);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 500) {
        setError("The AI key ran out of balance. Top up your Universal Key (Profile → Universal Key → Add Balance) and try again.");
      } else {
        setError("Couldn't generate the brief. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const onOpen = () => {
    setOpen(true);
    if (!brief) fetchBrief(false);
  };

  const copyBrief = async () => {
    try { await navigator.clipboard.writeText(brief); } catch (e) { /* noop */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <>
      <Button
        onClick={onOpen}
        className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold"
        data-testid="build-brief-button"
      >
        <Hammer className="h-4 w-4 mr-2" /> Build Brief
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm border-zinc-300 p-0 gap-0" data-testid="build-brief-dialog">
          <DialogHeader className="p-6 border-b border-zinc-200">
            <DialogTitle className="font-display text-xl font-black tracking-tight flex items-center gap-2">
              <Hammer className="h-5 w-5 text-[#002FA7]" /> Build Brief
            </DialogTitle>
            <p className="text-sm text-zinc-500 mt-1">
              A ready-to-paste prompt for Emergent or Claude — built from what real users want, like, and hate.
            </p>
          </DialogHeader>

          <div className="p-6">
            {loading && (
              <div className="flex items-center gap-3 text-zinc-500 font-mono text-sm py-16 justify-center" data-testid="brief-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Synthesizing insights into a build prompt…
              </div>
            )}

            {!loading && error && (
              <div className="border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800 flex gap-3" data-testid="brief-error">
                <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-500" />
                <div>
                  <p>{error}</p>
                  <Button onClick={() => fetchBrief(false)} variant="outline" size="sm" className="mt-3 rounded-sm" data-testid="brief-retry">
                    <RefreshCw className="h-3.5 w-3.5 mr-2" /> Retry
                  </Button>
                </div>
              </div>
            )}

            {!loading && !error && brief && (
              <>
                <div
                  className="bg-zinc-900 text-zinc-100 rounded-sm p-5 font-mono text-[12.5px] leading-relaxed max-h-[55vh] overflow-y-auto thin-scroll whitespace-pre-wrap"
                  data-testid="brief-content"
                >
                  {renderBrief(brief)}
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <Button onClick={copyBrief} className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold" data-testid="brief-copy">
                    {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                    {copied ? "Copied — paste it into a new build!" : "Copy prompt"}
                  </Button>
                  <Button onClick={() => fetchBrief(true)} variant="outline" className="rounded-sm border-zinc-300" data-testid="brief-regenerate">
                    <RefreshCw className="h-4 w-4 mr-2" /> Regenerate
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
